import React, { useRef, useState, useEffect } from "react";
import { FiAlertCircle, FiCheck, FiChevronRight, FiClock, FiFileText, FiChevronDown, FiUploadCloud } from "react-icons/fi";
import { twMerge } from "tailwind-merge";
import { motion } from "framer-motion";
import "./css/GlassIcons.css";

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 300, damping: 24 } },
};

/**
 * GlassIconsRentas — Componente dedicado a la lista de documentos
 * para el trámite de Rentas (Arrendamiento).
 * Permite gestionar de manera independiente estados como Vencido y Observado.
 */
const GlassIconsRentas = ({ items, className, show, setShow, setItemSeleccionado, data, estatusRentas, isBloqueado = false }) => {
  const scrollContainerRef = useRef(null);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);

  const checkScroll = () => {
    const el = scrollContainerRef.current;
    if (el) {
      const hasMoreToScroll = el.scrollHeight > el.clientHeight;
      const isNotAtBottom = el.scrollTop + el.clientHeight < el.scrollHeight - 15;
      setShowScrollIndicator(hasMoreToScroll && isNotAtBottom);
    }
  };

  useEffect(() => {
    const timer = setTimeout(checkScroll, 150);
    window.addEventListener("resize", checkScroll);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", checkScroll);
    };
  }, [items]);

  const handleScroll = () => {
    checkScroll();
  };

  const scrollToBottom = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior: "smooth"
      });
    }
  };

  const estatusDocNorm = (data?.nombre_estado || "").toUpperCase().trim();
  const estatusRentasNorm = (estatusRentas || data?.estatus_rentas || data?.tramite_estatus || "").toUpperCase().trim();
  const isVencidoRentas = estatusRentasNorm === "VENCIDO";
  const isConcluidoRentas = estatusRentasNorm === "CONCLUIDO" || estatusRentasNorm === "CONCLUIDA";
  const isObservadoDoc = estatusDocNorm.includes("OBSERVAD");
  const isObservadoYVencido = isObservadoDoc && isVencidoRentas;
  const isVencidoOConcluido = isVencidoRentas || isConcluidoRentas;

  return (
    <div className="relative w-full">
      <motion.div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        variants={containerVariants}
        initial="hidden"
        animate="show"
        className={twMerge(
          "w-full flex flex-col gap-3 select-none overflow-y-auto max-h-[420px] pr-2 custom-scrollbar",
          className
        )}
      >
        {items.map((item, index) => {
          let desc = (item.descripcion || "").toLowerCase().trim();

          // REGLA: Cuando en Rentas el trámite esté Observado y Vencido,
          // los documentos que salgan como "En Revisión" deben decir "Observado",
          // pero que no puedan hacer nada (SOLO EN ESTOS CASOS, NO EN OTROS).
          if (isObservadoYVencido && (desc === "en revisión" || desc === "en revision" || desc.includes("revis"))) {
            desc = "observado";
          }

          const isObservado = desc === "rechazado" || desc === "preregistro rechazado" || desc === "observado";
          const isAprobado = desc === "aprobado";
          const isEnRevision = desc === "en revisión" || desc === "en revision";
          const isPendiente = desc === "pendiente" || !desc;

          // Determinar estilo del Badge del Estado
          let badgeBg = "bg-slate-100 text-slate-700 border-slate-300";
          let statusText = "Pendiente";
          let statusIcon = <FiClock className="size-3.5" />;

          if (isObservado) {
            badgeBg = "bg-red-600 text-white border-transparent shadow-xs";
            statusText = "Observado";
            statusIcon = <FiAlertCircle className="size-3.5" />;
          } else if (isAprobado) {
            badgeBg = "bg-emerald-600 text-white border-transparent shadow-xs";
            statusText = "Aceptado";
            statusIcon = <FiCheck className="size-3.5" />;
          } else if (isEnRevision) {
            badgeBg = "bg-amber-500 text-white border-transparent shadow-xs";
            statusText = "En Revisión";
            statusIcon = <FiClock className="size-3.5" />;
          }

          // Si es clickeable (el usuario debe corregirlo)
          // Cuando sea Observado y Vencido (o esté vencido/concluido), NO debe permitir ninguna acción.
          const isClickable = isObservado && data?.nombre_estado !== "Preregistro Rechazado" && !isObservadoYVencido && !isVencidoOConcluido && !isBloqueado;

          return (
            <motion.div
              variants={itemVariants}
              key={index}
              className={twMerge(
                "w-full rounded-2xl p-4 sm:p-5 border bg-white border-slate-200 hover:border-slate-300 shadow-xs hover:shadow-md transition-all flex flex-col gap-4",
                isClickable && "cursor-pointer active:scale-[0.995]"
              )}
              onClick={() => {
                if (isClickable) {
                  setItemSeleccionado(item);
                  setShow(true);
                }
              }}
            >
              {/* Parte Superior: Icono y Nombre */}
              <div className="flex items-start gap-3.5 min-w-0">
                {/* Icono del documento */}
                <div className="size-11 rounded-2xl bg-slate-100 text-slate-700 flex items-center justify-center text-lg flex-shrink-0 border border-slate-200/80 shadow-xs">
                  {item.icon || <FiFileText className="size-5" />}
                </div>

                {/* Título y detalles */}
                <div className="min-w-0 flex-1">
                  <h5 className="text-sm sm:text-base font-bold text-slate-800 tracking-tight leading-snug break-words">
                    {item.label}
                  </h5>
                  {item.parte && item.parte !== "Único" && (
                    <span className="inline-block mt-1 text-xs font-semibold text-slate-600 bg-slate-100 px-2.5 py-0.5 rounded-lg border border-slate-200">
                      {item.parte}
                    </span>
                  )}
                </div>
              </div>

              {/* Parte Inferior: Badge de estado y Acción */}
              <div className="flex items-center justify-between gap-3 pt-3 border-t border-slate-100">
                {/* Badge de Estatus */}
                <span className={twMerge(
                  "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold border",
                  badgeBg
                )}>
                  {statusIcon}
                  <span>{statusText}</span>
                </span>

                {/* Botón de Acción si requiere corregirse (bloqueado si está vencido u observado+vencido) */}
                {isClickable && (
                  <div className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-xs sm:text-[13px] px-3.5 py-2 rounded-xl shadow-xs transition-all">
                    <FiUploadCloud className="size-4 shrink-0" />
                    <span>Subir archivo nuevo</span>
                    <FiChevronRight className="size-4 shrink-0" />
                  </div>
                )}
              </div>
            </motion.div>
          );
        })}
      </motion.div>

      {/* Flecha indicadora de más scroll hacia abajo */}
      {showScrollIndicator && (
        <button
          onClick={scrollToBottom}
          className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-white/95 backdrop-blur-sm hover:bg-slate-50 border border-slate-200/80 shadow-lg text-slate-500 hover:text-primary rounded-full size-9 flex items-center justify-center transition-all hover:scale-110 active:scale-90 animate-bounce z-10"
          title="Ver más documentos"
        >
          <FiChevronDown className="size-5" />
        </button>
      )}
    </div>
  );
};

export default GlassIconsRentas;
